import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api/v1`;

// ─────────────────────────────────────────────────────────────────────────
// Sprint — Isolated auth per role, split by SUPER_ADMIN vs STORE_ADMIN.
//
// LocalStorage layout (per-origin, shared across tabs of the same browser):
//   customer_access_token    / customer_refresh_token    / customer_user    / customer_store_id
//   super_admin_access_token / super_admin_refresh_token / super_admin_user / super_admin_store_id
//   store_admin_access_token / store_admin_refresh_token / store_admin_user / store_admin_store_id
//   delivery_access_token    / delivery_refresh_token    / delivery_user    / delivery_store_id
//
// SessionStorage marker (per-tab):
//   bb_admin_ns = "super_admin" | "store_admin"
//   Set on login so a tab that logs in as one role never accidentally picks
//   up the other role's token when navigating to an ambiguous admin URL
//   (like `/admin/{some-slug}` which SUPER_ADMIN can also drill into).
//
// Four fully independent Axios instances, one per namespace, each strictly
// bound to its own localStorage keys. There is NO cross-namespace token
// reading anywhere.
//
// The `api` export is a Proxy that dispatches to the client matching the
// current tab's URL. Tabs open on different roles route through different
// clients transparently — SUPER_ADMIN in one tab and STORE_ADMIN in another
// coexist without collision.
// ─────────────────────────────────────────────────────────────────────────

const NS_CUSTOMER    = "customer";
const NS_SUPER_ADMIN = "super_admin";
const NS_STORE_ADMIN = "store_admin";
const NS_DELIVERY    = "delivery";

const TAB_MARK_KEY = "bb_admin_ns";     // sessionStorage — per-tab admin role

const roleToNs = (role) => {
  if (role === "CUSTOMER") return NS_CUSTOMER;
  if (role === "DELIVERY_PARTNER" || role === "DELIVERY_MANAGER") return NS_DELIVERY;
  if (role === "SUPER_ADMIN") return NS_SUPER_ADMIN;
  return NS_STORE_ADMIN;                // STORE_ADMIN / STORE_OWNER / STORE_MANAGER / CASHIER / PACKER
};

const _readTabMark = () => {
  try { return sessionStorage.getItem(TAB_MARK_KEY); } catch { return null; }
};

const _writeTabMark = (ns) => {
  try {
    if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) {
      sessionStorage.setItem(TAB_MARK_KEY, ns);
    }
  } catch { /* noop */ }
};

const _clearTabMark = () => {
  try { sessionStorage.removeItem(TAB_MARK_KEY); } catch { /* noop */ }
};

const pathToNsRaw = (pathname) => {
  const p = pathname || "";
  if (p.startsWith("/delivery")) return NS_DELIVERY;

  // Explicit SUPER_ADMIN URL prefix — always super_admin, and we sticky the
  // per-tab marker so subsequent drill-down into `/admin/{slug}/*` in the same
  // tab keeps using the SA token (SA viewing a store).
  if (p.startsWith("/admin/platform")) {
    _writeTabMark(NS_SUPER_ADMIN);
    return NS_SUPER_ADMIN;
  }

  // Ambiguous admin URL (`/admin/{slug}/*` or `/pos`): honour the tab marker
  // set at login time. If no marker, fall back to whichever admin session
  // exists in localStorage.
  if (p.startsWith("/admin") || p.startsWith("/pos")) {
    const mark = _readTabMark();
    if (mark === NS_SUPER_ADMIN || mark === NS_STORE_ADMIN) return mark;

    const hasStoreAdmin = !!(typeof window !== "undefined"
      && localStorage.getItem(`${NS_STORE_ADMIN}_access_token`));
    const hasSuperAdmin = !!(typeof window !== "undefined"
      && localStorage.getItem(`${NS_SUPER_ADMIN}_access_token`));

    if (hasStoreAdmin) return NS_STORE_ADMIN;
    if (hasSuperAdmin) return NS_SUPER_ADMIN;
    return NS_STORE_ADMIN;               // sensible default → will 401 → /login
  }

  return NS_CUSTOMER;
};

export const pathToNs = (pathname) =>
  pathToNsRaw(pathname !== undefined ? pathname : (typeof window !== "undefined" ? window.location.pathname : ""));

const keysFor = (ns) => ({
  access:  `${ns}_access_token`,
  refresh: `${ns}_refresh_token`,
  user:    `${ns}_user`,
  store:   `${ns}_store_id`,
});

const readUser = (ns) => {
  try { return JSON.parse(localStorage.getItem(keysFor(ns).user) || "null"); } catch { return null; }
};

const clearNs = (ns) => {
  const K = keysFor(ns);
  localStorage.removeItem(K.access);
  localStorage.removeItem(K.refresh);
  localStorage.removeItem(K.user);
  localStorage.removeItem(K.store);
};

// ─── One-shot migrations ────────────────────────────────────────────────
// 1) Pre-split `admin_*` unified keys → new `super_admin_*` / `store_admin_*`
//    based on the stored user's role.
(() => {
  try {
    const legacyAccess  = localStorage.getItem("admin_access_token");
    const legacyUserRaw = localStorage.getItem("admin_user");
    if (!legacyAccess || !legacyUserRaw) return;
    const legacyRefresh = localStorage.getItem("admin_refresh_token");
    const legacyStore   = localStorage.getItem("admin_store_id");
    const legacyShadow  = localStorage.getItem("admin_shadow");
    const user = JSON.parse(legacyUserRaw);
    const ns = roleToNs(user?.role);
    const K = keysFor(ns);
    if (!localStorage.getItem(K.access)) {
      localStorage.setItem(K.access, legacyAccess);
      if (legacyRefresh) localStorage.setItem(K.refresh, legacyRefresh);
      localStorage.setItem(K.user, legacyUserRaw);
      if (legacyStore) localStorage.setItem(K.store, legacyStore);
    }
    // Legacy impersonation shadow (only meaningful for SA).
    if (legacyShadow && ns === NS_SUPER_ADMIN && !localStorage.getItem("super_admin_shadow")) {
      localStorage.setItem("super_admin_shadow", legacyShadow);
    }
    ["admin_access_token","admin_refresh_token","admin_user","admin_store_id","admin_shadow"]
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* noop */ }
})();

// 2) Very old `bb_*` unified keys → new namespaced keys.
(() => {
  try {
    const legacyAccess  = localStorage.getItem("bb_access_token");
    const legacyUserRaw = localStorage.getItem("bb_user");
    if (!legacyAccess || !legacyUserRaw) return;
    const legacyRefresh = localStorage.getItem("bb_refresh_token");
    const legacyStore   = localStorage.getItem("bb_store_id");
    const legacyUser    = JSON.parse(legacyUserRaw);
    const ns = roleToNs(legacyUser?.role);
    const K = keysFor(ns);
    if (!localStorage.getItem(K.access)) {
      localStorage.setItem(K.access, legacyAccess);
      if (legacyRefresh) localStorage.setItem(K.refresh, legacyRefresh);
      localStorage.setItem(K.user, legacyUserRaw);
      if (legacyStore) localStorage.setItem(K.store, legacyStore);
    }
    ["bb_access_token","bb_refresh_token","bb_user","bb_store_id"]
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* noop */ }
})();

// ─── Per-namespace Axios clients ───────────────────────────────────────
// Silent refresh: on any 401 from a request, try refreshing once and retry.
// Concurrent 401s per namespace share a single in-flight promise.
const _refreshInFlight = {
  [NS_CUSTOMER]:    null,
  [NS_SUPER_ADMIN]: null,
  [NS_STORE_ADMIN]: null,
  [NS_DELIVERY]:    null,
};

async function _refreshForNs(ns) {
  if (_refreshInFlight[ns]) return _refreshInFlight[ns];
  const K = keysFor(ns);
  const refreshToken = localStorage.getItem(K.refresh);
  if (!refreshToken) return Promise.resolve(false);
  _refreshInFlight[ns] = (async () => {
    try {
      const resp = await axios.post(
        `${API_BASE}/auth/refresh`,
        null,
        { params: { body_refresh: refreshToken }, headers: { "Content-Type": "application/json" } }
      );
      const { access_token, refresh_token } = resp.data || {};
      if (!access_token || !refresh_token) return false;
      localStorage.setItem(K.access, access_token);
      localStorage.setItem(K.refresh, refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { _refreshInFlight[ns] = null; }, 0);
    }
  })();
  return _refreshInFlight[ns];
}

const makeClient = (ns) => {
  const inst = axios.create({ baseURL: API_BASE });

  inst.interceptors.request.use((cfg) => {
    const K = keysFor(ns);
    const t = localStorage.getItem(K.access);
    if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
    const s = localStorage.getItem(K.store);
    if (s && !cfg.headers["X-Store-Id"]) cfg.headers["X-Store-Id"] = s;
    return cfg;
  });

  inst.interceptors.response.use(
    (r) => r,
    async (err) => {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.error?.message || err?.response?.data?.detail || "";
      const cfg    = err?.config || {};
      const url    = cfg.url || "";

      const isAuthCall = /\/auth\/(login|register|refresh|logout|forgot|reset|otp)/.test(url);

      // Silent refresh once on 401.
      if (status === 401 && !isAuthCall && !cfg._retriedAfterRefresh) {
        const ok = await _refreshForNs(ns);
        if (ok) {
          cfg._retriedAfterRefresh = true;
          const K = keysFor(ns);
          const t = localStorage.getItem(K.access);
          if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
          return inst.request(cfg);
        }
      }

      if (typeof window !== "undefined") {
        const activeNs = pathToNs();
        const onLogin  = window.location.pathname === "/login";

        // Only react to auth failures if the failing request's ns matches the
        // currently-active tab ns — a background poll from a hidden role must
        // never boot another logged-in role out of its tab.
        const isActiveHere = activeNs === ns && !onLogin;

        if (status === 401 && isActiveHere && !isAuthCall) {
          clearNs(ns);
          if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) _clearTabMark();
          window.location.href = "/login";
        } else if (status === 403 && /permission/i.test(msg) && isActiveHere) {
          const cur = readUser(ns);
          if (cur?.role === "SUPER_ADMIN" || cur?.role === "STORE_ADMIN" || cur?.role === "STORE_OWNER") {
            clearNs(ns);
            if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) _clearTabMark();
            window.location.href = "/login?stale=1";
          }
        }
      }
      return Promise.reject(err);
    }
  );

  return inst;
};

const clients = {
  [NS_CUSTOMER]:    makeClient(NS_CUSTOMER),
  [NS_SUPER_ADMIN]: makeClient(NS_SUPER_ADMIN),
  [NS_STORE_ADMIN]: makeClient(NS_STORE_ADMIN),
  [NS_DELIVERY]:    makeClient(NS_DELIVERY),
};

const clientForNs = (ns) => clients[ns] || clients[NS_CUSTOMER];

// Public: pick the client explicitly by role (rare — use inside login flows).
export const apiFor = (role) => clientForNs(roleToNs(role));

// Public: dispatching client that follows the current tab's URL.
export const api = new Proxy({}, {
  get(_, prop) {
    const target = clientForNs(pathToNs());
    const v = target[prop];
    return typeof v === "function" ? v.bind(target) : v;
  },
});

// ─── Auth helpers ──────────────────────────────────────────────────────
export const auth = {
  // Reads default to the current tab's role (path-derived).
  getAccess:   () => localStorage.getItem(keysFor(pathToNs()).access),
  getRefresh:  () => localStorage.getItem(keysFor(pathToNs()).refresh),
  getUser:     () => readUser(pathToNs()),
  getStoreId:  () => localStorage.getItem(keysFor(pathToNs()).store),

  // Explicit, role-aware reads.
  getAccessFor:  (role) => localStorage.getItem(keysFor(roleToNs(role)).access),
  getUserFor:    (role) => readUser(roleToNs(role)),
  getStoreIdFor: (role) => localStorage.getItem(keysFor(roleToNs(role)).store),

  // Writes route by the user's ROLE — never by URL — so logging in as one
  // role can never overwrite another role's active session. Also updates the
  // per-tab marker for admin logins so subsequent navigation on this tab
  // picks the correct token even when the URL is ambiguous.
  setSession: ({ user, tokens }) => {
    const ns = roleToNs(user?.role);
    const K = keysFor(ns);
    localStorage.setItem(K.access, tokens.access_token);
    localStorage.setItem(K.refresh, tokens.refresh_token);
    localStorage.setItem(K.user, JSON.stringify(user));
    if (user.store_id) localStorage.setItem(K.store, user.store_id);
    if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) _writeTabMark(ns);
  },
  setUser: (user) => {
    const ns = user?.role ? roleToNs(user.role) : pathToNs();
    localStorage.setItem(keysFor(ns).user, JSON.stringify(user));
  },
  setStoreId: (storeId, role) => {
    const ns = role ? roleToNs(role) : pathToNs();
    if (storeId) localStorage.setItem(keysFor(ns).store, storeId);
  },

  // Logout only wipes the CURRENT tab's role. Other roles keep their session.
  clear:     () => { const ns = pathToNs(); clearNs(ns); if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) _clearTabMark(); },
  clearFor:  (role) => { const ns = roleToNs(role); clearNs(ns); if (ns === NS_SUPER_ADMIN || ns === NS_STORE_ADMIN) _clearTabMark(); },
  clearAll:  () => { clearNs(NS_CUSTOMER); clearNs(NS_SUPER_ADMIN); clearNs(NS_STORE_ADMIN); clearNs(NS_DELIVERY); _clearTabMark(); },

  // Tab marker helpers (used by AuthContext on login / logout).
  markTabAs: (role) => _writeTabMark(roleToNs(role)),
  clearTabMark: _clearTabMark,

  currentNs: pathToNs,
};

// Ephemeral client with an explicit bearer (used by POS to act on behalf of a
// customer without touching staff session).
export const clientFor = (token, storeId) => {
  const inst = axios.create({ baseURL: API_BASE });
  inst.interceptors.request.use((c) => {
    if (token) c.headers.Authorization = `Bearer ${token}`;
    if (storeId) c.headers["X-Store-Id"] = storeId;
    return c;
  });
  return inst;
};

export const money = (v, currency = "₹") => {
  const n = Number(v || 0);
  return `${currency}${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.error;
  if (typeof d?.message === "string") return d.message;
  if (typeof e?.message === "string") return e.message;
  return fallback;
};
